import { withConsentUrl } from "@/app/utils/consent-url"

const BASE_API_V2 = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/consent/v2`
const BASE_API_V3 = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/common/consent/v3`

export type ConsentStatus = "granted" | "refused" | "withdrawn"
export type ConsentChannel = "phone" | "email"
export type ConsentQuoteType = "loan" | "health" | "travel_insurance"
export type ConsentAccordQuoteType =
    | ConsentQuoteType
    | "provident_deces"
    | "provident_obseques"
    | "provident_dependance"
export type ConsentHolder = "reassurez-moi" | "partners" | "prevoir"

export interface IConsentAccordChannel {
    status: ConsentStatus
    collected_at: string
    expires_at: string | null
    valid: boolean
}

// a channel key is absent when the product never collects it, null when the product has no row for it yet
export interface IConsentAccordProduct {
    quote_type: ConsentAccordQuoteType
    requested_at: string | null
    holder: ConsentHolder
    granted: boolean
    granted_at: string | null
    expires_at: string | null
    withdrawn_at: string | null
    channels: Partial<Record<ConsentChannel, IConsentAccordChannel | null>>
}

export interface IConsentAccords {
    email: string
    updated_at: string | null
    products: IConsentAccordProduct[]
}

// tracking of our emails (open pixel, clicks): one answer for the person, whatever the product
export interface IEmailTrackingConsent {
    granted: boolean
    status: ConsentStatus | null
    collected_at: string | null
    expires_at: string | null
}

export interface IConsentAccordChange {
    quote_type: ConsentAccordQuoteType
    granted: boolean
}

// the email the visitor clicked (URL ?fe=): stored with the consent rows to tie a change to the email behind it
function withFromEmail<T extends Record<string, unknown>>(payload: T, fromEmail: string | null): T {
    return fromEmail ? { ...payload, from_email: fromEmail } : payload
}

export async function getConsentAccords(token: string): Promise<IConsentAccords | null> {
    try {
        const response = await fetch(`${BASE_API_V3}/${token}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
        })
        if (!response.ok) return null
        return await response.json()
    } catch (error) {
        console.error("Error fetching consent accords:", error)
        return null
    }
}

// one accord per product: the server grants or withdraws phone and email together
export async function putConsentAccords(
    token: string,
    products: IConsentAccordChange[],
    fromEmail: string | null = null
): Promise<IConsentAccords | null> {
    try {
        const response = await fetch(`${BASE_API_V3}/${token}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({ products }, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return null
        const result = await response.json().catch(() => null)
        return result?.success === true ? result : null
    } catch (error) {
        console.error("Error saving consent accords:", error)
        return null
    }
}

export async function grantPhoneConsent(token: string, fromEmail: string | null = null): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/${token}/grant-phone`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({}, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error granting phone consent:", error)
        return false
    }
}

export async function getEmailTrackingConsent(token: string): Promise<IEmailTrackingConsent | null> {
    try {
        const response = await fetch(`${BASE_API_V3}/${token}/email-tracking`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
        })
        if (!response.ok) return null
        return await response.json()
    } catch (error) {
        console.error("Error fetching email tracking consent:", error)
        return null
    }
}

export async function putEmailTrackingConsent(
    token: string,
    granted: boolean,
    fromEmail: string | null = null
): Promise<IEmailTrackingConsent | null> {
    try {
        const response = await fetch(`${BASE_API_V3}/${token}/email-tracking`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({ granted }, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return null
        const result = await response.json().catch(() => null)
        return result?.success === true ? result : null
    } catch (error) {
        console.error("Error saving email tracking consent:", error)
        return null
    }
}

// bouton « J'accepte » de l'email de collecte envoyé à la base existante
export async function grantEmailTrackingConsent(token: string, fromEmail: string | null = null): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/${token}/grant-email-tracking`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl(withFromEmail({}, fromEmail))),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error granting email tracking consent:", error)
        return false
    }
}

/** Choix publicitaire du bandeau : persisté avec le lead, jamais réaffiché côté client. */
export async function postAdsConsent(
    quoteToken: string,
    quoteType: ConsentQuoteType,
    status: "granted" | "refused"
): Promise<boolean> {
    try {
        const response = await fetch(`${BASE_API_V2}/ads`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(withConsentUrl({ quote_token: quoteToken, quote_type: quoteType, status })),
            cache: "no-store",
        })
        if (!response.ok) return false
        const result = await response.json().catch(() => null)
        return result?.success === true
    } catch (error) {
        console.error("Error posting ads consent:", error)
        return false
    }
}
